import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, Rss, ArrowLeft, Youtube, Instagram, Image as ImageIcon, BarChart3 } from "lucide-react";

// Build a tracking URL: /path → {base}/path?utm_source=...&utm_medium=...&utm_campaign=...&utm_content=...
const utm = (base, path, { source, medium, campaign, content }) => {
  const full = path.startsWith("http") ? path : `${base}${path}`;
  const sep = full.includes("?") ? "&" : "?";
  const q = new URLSearchParams({
    utm_source: source,
    utm_medium: medium,
    utm_campaign: campaign,
    ...(content ? { utm_content: content } : {})
  }).toString();
  // Preserve hash fragments (e.g. /#signup) — put query BEFORE the hash
  if (full.includes("#")) {
    const [hashless, hash] = full.split("#");
    return `${hashless}${hashless.includes("?") ? "&" : "?"}${q}#${hash}`;
  }
  return `${full}${sep}${q}`;
};

const CaptionBlock = ({ id, label, text }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      setCopied(false);
    }
  };
  return (
    <div className="bg-white border border-[#CCFBF1] rounded-xl overflow-hidden" data-testid={`caption-${id}`}>
      <div className="flex items-center justify-between px-4 py-2.5 bg-[#F0F5F4] border-b border-[#CCFBF1]">
        <p className="text-[#25a1c2] text-[11px] font-bold uppercase tracking-widest">{label}</p>
        <button
          onClick={onCopy}
          className="inline-flex items-center gap-1.5 text-xs text-[#25a1c2] hover:text-[#042F2E] font-medium"
          data-testid={`copy-${id}`}
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="whitespace-pre-wrap break-words p-4 text-sm text-[#068fa2] font-sans leading-relaxed">
        {text}
      </pre>
    </div>
  );
};

// Each essay is a builder: given origin, it returns all caption blocks with UTMs baked in.
const ESSAYS = [
  {
    key: "toxic-positivity",
    title: "The Quiet Violence of Toxic Positivity",
    slug: "toxic-positivity",
    heroColor: "from-[#068fa2] to-[#042F2E]",
    audioPath: "/downloads/audio/toxic-positivity.m4a",
    productPath: "/emergency-calm-kit",
    productName: "Emergency Calm Kit ($11)",
    productBlurb: "20 printable cards built for these exact moments",
    pinterestTitle: "10 Phrases That Are Actually Toxic Positivity (And What to Say Instead)",
    pinterestDescription:
      "Toxic positivity sounds like love but lands like erasure. 10 phrases to recognize it — in others and in the voice in your own head — plus the HYGIENE practice to replace them. Real tools for real women in the messy middle. | mind body spirit HYGIENE tools | Temple Franklin",
    hashtagsYT:
      "#toxicpositivity #realwomen #mindbodyspirithygiene #messymiddle #emotionalhealth #healingjourney",
    hashtagsIG:
      "#ToxicPositivity #MindBodyHygiene #MessyMiddle #RealTalk #NervousSystemHealing #EmotionalHygiene #HealingJourney #SelfCompassion",
    ytTags:
      "toxic positivity, mind body spirit hygiene, temple franklin, messy middle, emotional health, nervous system, healing, real women, self care, therapy, grief, mental health",
    youtubeIntro: `Toxic positivity sounds like love. It lands like erasure.

In this essay I break down 10 phrases to recognize toxic positivity — in others, and in the quiet voice in your own head — and the HYGIENE practice to replace them.`,
    igBody: `"Everything happens for a reason." 🫠

Sounds like love. Lands like erasure.

Toxic positivity isn't kindness — it's the quiet violence of bypassing someone's truth. And we don't just receive it from others. We do it to ourselves. Every. Day.

10 phrases to recognize it 👇 (save this)

1. Everything happens for a reason
2. Just stay positive
3. It could be worse
4. Good vibes only
5. Look on the bright side
6. At least…
7. God doesn't give you more than you can handle
8. You'll look back on this and laugh
9. What's meant for you won't miss you
10. Be grateful — some people have it worse

👉 Swap "at least" for "and also."
👉 Swap "you should" for "what do you need?"
👉 Swap "don't worry" for "I'm here."`
  },
  {
    key: "messy-middle",
    title: "The Messy Middle & Your Personal Code of Conduct",
    slug: "messy-middle-code-of-conduct",
    heroColor: "from-[#25a1c2] to-[#068fa2]",
    audioPath: "/downloads/audio/messy-middle.m4a",
    productPath: "/shop",
    productName: "Personal Code of Conduct Workbook ($17)",
    productBlurb: "38 acronym frameworks to build your Code BEFORE sh!t hits the fan",
    pinterestTitle:
      "13 Messy Middle Moments Every Woman Faces (And How to Prepare Before They Happen)",
    pinterestDescription:
      "You cannot build your response in the moment. You have to build it BEFORE. 13 real-life tests of the messy middle + how to build your Personal Code of Conduct. Real tools for real women. | mind body spirit HYGIENE tools | Temple Franklin",
    hashtagsYT:
      "#personalcodeofconduct #messymiddle #mindbodyspirithygiene #templefranklin #nervoussystemregulation #selfleadership",
    hashtagsIG:
      "#PersonalCode #MessyMiddle #MindBodyHygiene #RealWomen #SelfLeadership #BoundarySetting #NervousSystemHealing",
    ytTags:
      "messy middle, personal code of conduct, mind body spirit hygiene, temple franklin, boundaries, nervous system regulation, self leadership, women, real tools, healing, resilience",
    youtubeIntro: `You are never not in the middle of something.

Middle of a marriage. Middle of a grief. Middle of a Tuesday, with groceries melting in the back seat. The middle is where the tests come — and you cannot build your response in the moment. You have to build it BEFORE.

13 real-life tests of the messy middle + how to build your Personal Code of Conduct before sh!t hits the fan.`,
    igBody: `You cannot build your response in the moment.

You have to build it BEFORE.

That's what a Personal Code of Conduct is for — not a vision board. An operating system. Built in calm. Deployed in chaos. 🛡️

13 messy-middle tests every woman I know has lived through:

1. Red light runner
2. Divorce + your kid gets sick
3. The interrupter on the call
4. Gaslit in a vulnerable moment
5. Grocery-store meltdown
6. "One more thing" at 4:55pm
7. The family member who brings up THE topic
8. Partner dismisses your breakthrough
9. "At least they lived a long life" after loss
10. "You used to be so fun" postpartum
11. Group-gift Venmo pressure
12. Period wave before a presentation
13. Your depleted self + your crying teen

You are never NOT in the middle. The only question is whether you meet it armed with a Code — or barefoot, again.`
  }
];

// Given origin, produce the 5 ready-to-paste caption strings for one essay.
const buildCaptions = (base, e) => {
  const campaign = e.key;
  const blogYT   = utm(base, `/blog/${e.slug}`,     { source: "youtube",   medium: "description", campaign, content: "essay-link" });
  const prodYT   = utm(base, e.productPath,          { source: "youtube",   medium: "description", campaign, content: "product-link" });
  const signupYT = utm(base, "/#signup",             { source: "youtube",   medium: "description", campaign, content: "email-signup" });

  const blogIG   = utm(base, `/blog/${e.slug}`,     { source: "instagram", medium: "bio",         campaign });

  const pinLink  = utm(base, `/blog/${e.slug}`,     { source: "pinterest", medium: "pin",         campaign });

  return {
    ytTitle: `${e.title} | Temple Franklin`,
    ytDescription: `${e.youtubeIntro}

If this landed for you, the ${e.productName} is ${e.productBlurb}: ${prodYT}

Read the full essay: ${blogYT}

Start the free 52-week journey: ${signupYT}

${e.hashtagsYT}`,
    ytTags: e.ytTags,
    igCaption: `${e.igBody}

Full audio essay in my bio → ${blogIG}

Real tools. Real women. In the messy middle. 🤍

${e.hashtagsIG}`,
    pinTitle: e.pinterestTitle,
    pinDescription: e.pinterestDescription,
    pinLink
  };
};


export default function Distribute() {
  useEffect(() => {
    document.title = "Distribute Hub — Internal | Mind Body Spirit HYGIENE™ Tools";
    // Prevent indexing
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const rssUrl = `${origin}/rss.xml`;
  const essaysWithCaptions = useMemo(
    () => ESSAYS.map((e) => ({ ...e, captions: buildCaptions(origin, e) })),
    [origin]
  );

  return (
    <div className="bg-[#efeae7] min-h-screen" data-testid="distribute-page">
      <header className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-xl border-b border-[#CCFBF1]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-[#25a1c2] hover:text-[#042F2E] text-sm font-medium inline-flex items-center gap-1.5">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#25a1c2] bg-[#F0F5F4] px-2.5 py-1 rounded-full">
            Internal · Not Indexed
          </span>
        </div>
      </header>

      <section className="pt-28 pb-12">
        <div className="max-w-6xl mx-auto px-6">
          <p className="font-sans text-xs tracking-[0.2em] uppercase text-[#25a1c2] font-medium mb-3">Temple's Distribution Hub</p>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tighter text-[#042F2E] mb-4" data-testid="distribute-title">
            Copy. Paste. Post.
          </h1>
          <p className="font-sans text-[#068fa2] max-w-2xl mb-6">
            Every new audio essay ships with ready-to-post captions for YouTube, Instagram, and Pinterest.
            Buffer also auto-drafts from your RSS feed below.
          </p>

          <div className="bg-[#042F2E] rounded-2xl p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4 mb-4" data-testid="rss-block">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-[#F59E0B] rounded-lg flex items-center justify-center">
                <Rss className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-[#5EEAD4] text-xs font-bold uppercase tracking-widest">RSS Feed for Buffer</p>
                <p className="text-white/60 text-xs">Paste this into Buffer → Content → RSS Feeds</p>
              </div>
            </div>
            <div className="flex-1 flex items-center bg-white/10 rounded-lg px-3 py-2 border border-white/10">
              <code className="text-[#5EEAD4] text-xs md:text-sm truncate flex-1" data-testid="rss-url">{rssUrl}</code>
              <button
                onClick={() => navigator.clipboard.writeText(rssUrl)}
                className="ml-2 bg-[#5EEAD4] hover:bg-white text-[#042F2E] text-xs font-bold rounded-md px-3 py-1.5"
                data-testid="copy-rss-url"
              >
                Copy
              </button>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3" data-testid="utm-note">
            <BarChart3 className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="text-[13px] text-amber-900 leading-relaxed">
              <strong>UTM tracking is baked in.</strong> Every link below is auto-tagged with{" "}
              <code className="bg-amber-100 px-1 rounded text-[11px]">utm_source</code>,{" "}
              <code className="bg-amber-100 px-1 rounded text-[11px]">utm_medium</code>, and{" "}
              <code className="bg-amber-100 px-1 rounded text-[11px]">utm_campaign</code>. When someone
              clicks through from YouTube vs Instagram vs Pinterest, you'll see it inside GA4 → Reports → Traffic Acquisition.
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-6 space-y-14">
          {essaysWithCaptions.map((e) => (
            <div key={e.key} data-testid={`essay-block-${e.key}`}>
              <div className={`bg-gradient-to-r ${e.heroColor} rounded-2xl p-6 md:p-7 mb-6`}>
                <p className="text-white/60 text-[10px] font-bold uppercase tracking-widest mb-1.5">Audio Essay</p>
                <h2 className="font-serif text-2xl md:text-3xl text-white">{e.title}</h2>
                <div className="flex flex-wrap items-center gap-3 mt-3 text-white/80 text-xs">
                  <a href={`/blog/${e.slug}`} className="underline hover:text-[#5EEAD4]">View live post →</a>
                  <a href={e.audioPath} className="underline hover:text-[#5EEAD4]">Download audio →</a>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Youtube className="w-4 h-4 text-[#DC2626]" />
                    <p className="font-serif text-lg text-[#042F2E]">YouTube</p>
                  </div>
                  <CaptionBlock id={`${e.key}-yt-title`} label="Video Title" text={e.captions.ytTitle} />
                  <CaptionBlock id={`${e.key}-yt-desc`} label="Description (UTM tagged)" text={e.captions.ytDescription} />
                  <CaptionBlock id={`${e.key}-yt-tags`} label="Tags" text={e.captions.ytTags} />
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Instagram className="w-4 h-4 text-[#E1306C]" />
                    <p className="font-serif text-lg text-[#042F2E]">Instagram / Reel Caption</p>
                  </div>
                  <CaptionBlock id={`${e.key}-ig`} label="Caption (UTM tagged)" text={e.captions.igCaption} />
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <ImageIcon className="w-4 h-4 text-[#E60023]" />
                    <p className="font-serif text-lg text-[#042F2E]">Pinterest</p>
                  </div>
                  <CaptionBlock id={`${e.key}-pin-title`} label="Pin Title" text={e.captions.pinTitle} />
                  <CaptionBlock id={`${e.key}-pin-desc`} label="Pin Description" text={e.captions.pinDescription} />
                  <CaptionBlock id={`${e.key}-pin-link`} label="Destination Link (UTM tagged)" text={e.captions.pinLink} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <footer className="py-10 bg-[#042F2E]">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="font-sans text-[#5EEAD4] text-xs">Internal hub — not indexed. © 2026 Mind Body Spirit HYGIENE™ Tools.</p>
        </div>
      </footer>
    </div>
  );
}
